import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';
import styles from './CodingGameSplitLayout.module.css';

// Timer component that displays countdown
function Timer({ timeRemaining, onTimeUp }) {
  const [remainingMs, setRemainingMs] = useState(timeRemaining);

  useEffect(() => {
    setRemainingMs(timeRemaining);
  }, [timeRemaining]);

  useEffect(() => {
    if (remainingMs <= 0) {
      onTimeUp?.();
      return;
    }

    const interval = setInterval(() => {
      setRemainingMs((prev) => {
        const newValue = prev - 1000;
        if (newValue <= 0) {
          clearInterval(interval);
          onTimeUp?.();
          return 0;
        }
        return newValue;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [remainingMs, onTimeUp]);

  // Format time as HH:MM:SS
  const formatTime = (ms) => {
    if (ms <= 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const isLowTime = remainingMs < 300000; // Less than 5 minutes

  return (
    <div className={`${styles.timer} ${isLowTime ? styles.timerLow : ''}`}>
      <span className={styles.timerIcon}>⏱️</span>
      <span className={styles.timerValue}>{formatTime(remainingMs)}</span>
    </div>
  );
}

// Draggable resizer component
function Resizer({ direction, onResize }) {
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const startPos = direction === 'horizontal' ? e.clientX : e.clientY;

    const handleMouseMove = (moveEvent) => {
      const currentPos = direction === 'horizontal' ? moveEvent.clientX : moveEvent.clientY;
      const delta = currentPos - startPos;
      onResize(delta, moveEvent);
      // Trigger resize event for editors to adjust
      window.dispatchEvent(new Event('resize'));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Final resize event after drag ends
      window.dispatchEvent(new Event('resize'));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = direction === 'horizontal' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
  }, [direction, onResize]);

  return (
    <div
      className={`${styles.resizer} ${direction === 'horizontal' ? styles.resizerHorizontal : styles.resizerVertical}`}
      onMouseDown={handleMouseDown}
    />
  );
}

function TestCaseItem({ testCase }) {
  // Helper to format a single value (for LeetCode-style display)
  const formatSingleValue = (value) => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value}"`;
    if (Array.isArray(value)) return JSON.stringify(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Format input in LeetCode style: param1 = value1, param2 = value2
  const formatInputValue = (inputStr) => {
    if (!inputStr) return inputStr;

    try {
      const parsed = JSON.parse(inputStr);

      // If it's an object with parameter names as keys
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.entries(parsed)
          .map(([key, value]) => `${key} = ${formatSingleValue(value)}`)
          .join('\n');
      }

      // If it's a single value, just format it
      return formatSingleValue(parsed);
    } catch (e) {
      // Not valid JSON, return as-is
      return inputStr;
    }
  };

  // Format output value (keep JSON beautified)
  const formatOutputValue = (value) => {
    if (!value) return value;

    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return value;
    }
  };

  return (
    <div className={styles.testCaseItem}>
      <div className={styles.label}>Input</div>
      <pre className={styles.codeBlock}>{formatInputValue(testCase.input)}</pre>

      <div className={styles.label} style={{ marginTop: 8 }}>Expected Output</div>
      <pre className={styles.codeBlock}>{formatOutputValue(testCase.output)}</pre>

      {testCase.explanation && (
        <div style={{ marginTop: 8 }}>
          <div className={styles.label}>Explanation</div>
          <div className={styles.codeBlock}>{testCase.explanation}</div>
        </div>
      )}
    </div>
  );
}

function TestResultTabs({ results, selectedIndex, onSelectCase }) {
  // Filter out hidden test cases for display
  const visibleResults = results.filter(r => !r.hidden);

  return (
    <div className={styles.testCaseTabs}>
      {visibleResults.map((result, index) => (
        <button
          key={index}
          className={`${styles.testCaseTab} ${selectedIndex === index ? styles.testCaseTabActive : ''}`}
          onClick={() => onSelectCase(index)}
        >
          <span className={`${styles.testCaseCheckbox} ${result.passed ? styles.checkboxPassed : styles.checkboxFailed}`}>
            {result.passed ? '✓' : '✗'}
          </span>
          <span>Case {index + 1}</span>
        </button>
      ))}
    </div>
  );
}

function TestResultDetail({ result }) {
  if (!result) return null;

  // Helper to format a single value (for LeetCode-style display)
  const formatSingleValue = (value) => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value}"`;
    if (Array.isArray(value)) return JSON.stringify(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Format input in LeetCode style: param1 = value1, param2 = value2
  const formatInputValue = (inputStr) => {
    if (!inputStr) return inputStr;

    try {
      const parsed = JSON.parse(inputStr);

      // If it's an object with parameter names as keys
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.entries(parsed)
          .map(([key, value]) => `${key} = ${formatSingleValue(value)}`)
          .join('\n');
      }

      // If it's a single value, just format it
      return formatSingleValue(parsed);
    } catch (e) {
      // Not valid JSON, return as-is
      return String(inputStr);
    }
  };

  // Format output value (beautify JSON)
  const formatOutputValue = (value) => {
    if (value === null || value === undefined) return String(value);

    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return String(value);
    }
  };

  return (
    <div className={styles.testResultDetail}>
      <div className={styles.resultSection}>
        <div className={styles.resultSectionLabel}>Input</div>
        <div className={styles.resultBlock}>
          <pre className={styles.resultBlockValue}>{formatInputValue(result.input)}</pre>
        </div>
      </div>

      {result.actualOutput !== undefined && (
        <div className={styles.resultSection}>
          <div className={styles.resultSectionLabel}>
            Output
            {result.outputLine && result.outputLine > 0 && (
              <span className={styles.lineNumber}> (Line {result.outputLine})</span>
            )}
          </div>
          <div className={styles.resultBlock}>
            <pre className={styles.resultBlockValue}>{formatOutputValue(result.actualOutput)}</pre>
          </div>
        </div>
      )}

      <div className={styles.resultSection}>
        <div className={styles.resultSectionLabel}>Expected</div>
        <div className={styles.resultBlock}>
          <pre className={styles.resultBlockValue}>{formatOutputValue(result.expectedOutput)}</pre>
        </div>
      </div>

      {result.logs && result.logs.length > 0 && (
        <div className={styles.resultSection}>
          <div className={styles.resultSectionLabel}>Logs</div>
          <div className={styles.logsContainer}>
            {result.logs.map((log, idx) => (
              <div key={idx} className={styles.logEntry}>
                <span className={styles.logLine}>Line {log.line}:</span>
                <span className={styles.logValue}>{log.value}</span>
                <span className={styles.logCategory}>[{log.category}]</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.taskCount && (
        <div className={styles.taskCountSection}>
          <span className={styles.taskCountItem}>Time: {result.taskCount.time}ms</span>
          <span className={styles.taskCountItem}>Memory: {result.taskCount['MB-seconds']} MB-s</span>
        </div>
      )}

      {result.error && (
        <div className={styles.resultSection}>
          <div className={styles.resultSectionLabel}>Error</div>
          <div className={`${styles.resultBlock} ${styles.errorBlock}`}>
            <div className={styles.resultBlockValue}>{result.error}</div>
            {result.error.toLowerCase().includes('timed out') && (
              <div className={styles.errorHint}>
                Your script took too long to execute. Consider optimizing your code by reducing loops, avoiding infinite loops, or simplifying complex operations.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ValidationErrorDisplay({ validationError }) {
  return (
    <div className={styles.validationError}>
      <div className={styles.validationErrorLine}>
        <span className={styles.validationLabel}>Line {validationError.line}:</span>
      </div>
      <div className={styles.validationErrorMessage}>
        {validationError.message}
      </div>
    </div>
  );
}

function OutputPanel({ output, isLoading, isMinimized, onToggleMinimize }) {
  const [selectedCaseIndex, setSelectedCaseIndex] = useState(0);

  const hasDetailedResults = output?.data?.results && Array.isArray(output.data.results);
  const hasValidationError = output?.validationError;
  const allPassed = hasDetailedResults && output.data.failedTests === 0;

  // Filter visible (non-hidden) results for detailed display
  const visibleResults = hasDetailedResults
    ? output.data.results.filter(r => !r.hidden)
    : [];

  // Get total passed/total tests from data
  const passedTests = output?.data?.passedTests || 0;
  const totalTests = output?.data?.totalTests || 0;

  // Reset selected case when output changes
  React.useEffect(() => {
    setSelectedCaseIndex(0);
  }, [output]);

  return (
    <div className={`${styles.outputSection} ${isMinimized ? styles.outputMinimized : ''}`}>
      <div className={styles.outputHeader} onClick={onToggleMinimize} style={{ cursor: 'pointer' }}>
        <span>Output</span>
        <button
          className={styles.minimizeButton}
          onClick={(e) => {
            e.stopPropagation();
            onToggleMinimize();
          }}
          aria-label={isMinimized ? 'Expand output' : 'Minimize output'}
        >
          {isMinimized ? '▲' : '▼'}
        </button>
      </div>
      {!isMinimized && (
        <div className={styles.outputBody}>
        {isLoading ? (
          <div className={styles.loadingSpinner}>
            <div className={styles.spinner}></div>
            <span>Running...</span>
          </div>
        ) : output ? (
          hasValidationError ? (
            <ValidationErrorDisplay validationError={output.validationError} />
          ) : hasDetailedResults ? (
            <div className={styles.testResultsContainer}>
              <div className={styles.testResultsStatus}>
                <span className={allPassed ? styles.statusAccepted : styles.statusFailed}>
                  {allPassed ? 'Accepted' : 'Wrong Answer'}
                </span>
                <span className={styles.testResultsSummary}>
                  {passedTests}/{totalTests} cases passed
                </span>
              </div>
              {visibleResults.length > 0 && (
                <>
                  <TestResultTabs
                    results={visibleResults}
                    selectedIndex={selectedCaseIndex}
                    onSelectCase={setSelectedCaseIndex}
                  />
                  <TestResultDetail result={visibleResults[selectedCaseIndex]} />
                </>
              )}
            </div>
          ) : (
            <div className={styles.outputContent}>
              <pre>{output.message}</pre>
            </div>
          )
        ) : (
          <div className={styles.outputPlaceholder}>
            Run your code to see the output here
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function CodingGameSplitLayout({
  questionTitle,
  questionBody,
  testCases,
  EditorComponent,
  editorProps,
  onRun,
  onSubmit,
  onBack,
  languageOptions,
  selectedLanguage,
  onLanguageChange,
  output,
  isRunning,
  timeRemaining,
  onTimeUp,
  attemptsLeft,
  maxAttempts,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [leftPaneWidth, setLeftPaneWidth] = useState(35); // percentage
  const [editorHeight, setEditorHeight] = useState(65); // percentage
  const [isOutputMinimized, setIsOutputMinimized] = useState(true); // Start minimized

  const containerRef = useRef(null);
  const rightPaneRef = useRef(null);

  const toggleFullscreen = () => setIsFullscreen((prev) => !prev);
  const toggleOutputMinimize = () => setIsOutputMinimized((prev) => !prev);

  // Auto-expand output panel when running or when new output arrives
  useEffect(() => {
    if (isRunning || output) {
      setIsOutputMinimized(false);
    }
  }, [isRunning, output]);

  // Dispatch resize event when fullscreen or output minimize changes so editor can recalculate dimensions
  useEffect(() => {
    // Small delay to let DOM update first
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
    return () => clearTimeout(timer);
  }, [isFullscreen, isOutputMinimized]);

  // Horizontal resizer between left and right panes
  const handleHorizontalResize = useCallback((delta, e) => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const newWidth = ((e.clientX - containerRef.current.getBoundingClientRect().left) / containerWidth) * 100;
    setLeftPaneWidth(Math.min(Math.max(newWidth, 20), 60));
  }, []);

  // Vertical resizer between editor and output
  const handleEditorResize = useCallback((delta, e) => {
    if (!rightPaneRef.current) return;
    const paneHeight = rightPaneRef.current.offsetHeight;
    const newHeight = ((e.clientY - rightPaneRef.current.getBoundingClientRect().top) / paneHeight) * 100;
    setEditorHeight(Math.min(Math.max(newHeight, 30), 85));
  }, []);

  return (
    <div ref={containerRef} className={`${styles.container} ${isFullscreen ? styles.fullscreen : ''}`}>
      <div className={styles.main}>
        {!isFullscreen && (
          <>
            <div
              className={`${styles.pane} ${styles.leftPane}`}
              style={{ width: `${leftPaneWidth}%` }}
            >
              <div className={styles.leftPaneContent}>
                <div className={styles.sectionHeader}>
                  <div className={styles.headerLeft}>
                    {onBack && (
                      <button
                        className={styles.backButton}
                        onClick={onBack}
                        aria-label="Back to Map"
                      >
                        <ArrowLeft className={styles.backIcon} />
                      </button>
                    )}
                    <span>{questionTitle}</span>
                  </div>
                  {timeRemaining != null && timeRemaining > 0 && (
                    <Timer timeRemaining={timeRemaining} onTimeUp={onTimeUp} />
                  )}
                </div>
                {/* Attempts remaining indicator */}
                {attemptsLeft != null && maxAttempts != null && (
                  <div className={styles.attemptsIndicator}>
                    <span className={styles.attemptsLabel}>Attempts:</span>
                    <span className={`${styles.attemptsValue} ${attemptsLeft <= 1 ? styles.attemptsLow : ''}`}>
                      {attemptsLeft} / {maxAttempts}
                    </span>
                  </div>
                )}
                <div className={styles.questionBody}>{questionBody}</div>

                <div className={styles.sectionHeader} style={{ marginTop: 16 }}>
                  <div>Test Cases</div>
                </div>
                <div className={styles.testCaseList}>
                  {testCases.map((tc) => (
                    <TestCaseItem key={tc.id} testCase={tc} />
                  ))}
                </div>
              </div>
            </div>

            <Resizer direction="horizontal" onResize={handleHorizontalResize} />
          </>
        )}

        <div
          ref={rightPaneRef}
          className={`${styles.pane} ${styles.rightPane}`}
          style={{ width: isFullscreen ? '100%' : `${100 - leftPaneWidth}%` }}
        >
          <div className={styles.editorSection} style={{ height: isOutputMinimized ? 'calc(100% - 44px)' : `${editorHeight}%`, flex: isOutputMinimized ? 1 : undefined }}>
            <div className={styles.editorHeader}>
              <div>
                {languageOptions && languageOptions.length > 0 && (
                  <select
                    className={styles.languageSelect}
                    value={selectedLanguage}
                    onChange={(e) => onLanguageChange?.(e.target.value)}
                  >
                    {languageOptions.map((lang) => (
                      <option key={lang} value={lang}>
                        {lang}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className={styles.actions}>
                <button
                  className={`${styles.button} ${styles.secondary}`}
                  onClick={onRun}
                  disabled={isRunning}
                >
                  {isRunning ? 'Running...' : 'Run'}
                </button>
                <button
                  className={styles.button}
                  onClick={onSubmit}
                  disabled={isRunning}
                >
                  Submit
                </button>
                <button
                  className={`${styles.button} ${styles.secondary} ${styles.iconButton}`}
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <Minimize2 size={16} />
                  ) : (
                    <Maximize2 size={16} />
                  )}
                </button>
              </div>
            </div>

            <div className={styles.editorBody}>
              <EditorComponent {...editorProps} />
            </div>
          </div>

          {!isOutputMinimized && (
            <Resizer direction="vertical" onResize={handleEditorResize} />
          )}

          <div style={{ height: isOutputMinimized ? 'auto' : `${100 - editorHeight}%`, minHeight: isOutputMinimized ? '40px' : undefined }}>
            <OutputPanel
              output={output}
              isLoading={isRunning}
              isMinimized={isOutputMinimized}
              onToggleMinimize={toggleOutputMinimize}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CodingGameSplitLayout;
